import type { ReactNode } from 'react';
import { classic42 } from '@risk/maps';
import type { GameState, SeatId, TerritoryId } from '@risk/engine';

/**
 * The continent-grouped list.
 *
 * This is not a fallback for a map that has not been drawn yet: for placing
 * armies and for the end-of-turn audit it is the better tool, because accuracy
 * beats prettiness when someone is reading numbers off a physical board.
 */
export function TerritoryList({
  game,
  seatId,
  selected,
  targets,
  onSelect,
  filter,
  trailing,
}: {
  game: GameState;
  seatId: SeatId | null;
  // Explicitly `| undefined`: with exactOptionalPropertyTypes, a caller
  // passing `cond ? fn : undefined` is otherwise a type error.
  selected?: TerritoryId | null | undefined;
  targets?: ReadonlySet<TerritoryId> | undefined;
  onSelect?: ((id: TerritoryId) => void) | undefined;
  filter?: ((id: TerritoryId) => boolean) | undefined;
  trailing?: ((id: TerritoryId) => ReactNode) | undefined;
}): ReactNode {
  const colorOf = (owner: SeatId | null): string =>
    game.seats.find((seat) => seat.id === owner)?.color ?? '#555b66';

  return (
    <div>
      {classic42.continents.map((continent) => {
        const ids = continent.territories.filter((id) => filter?.(id) ?? true);
        if (ids.length === 0) return null;

        const held = continent.territories.every(
          (id) => game.territories[id]?.owner === seatId && seatId !== null,
        );

        return (
          <section className="continent" key={continent.id}>
            <header>
              <strong>{continent.name}</strong>
              <span className="small muted">
                {held ? `held · +${continent.bonus}` : `+${continent.bonus}`}
              </span>
            </header>
            {ids.map((id) => {
              const territory = game.territories[id];
              const owner = territory?.owner ?? null;
              const mine = owner !== null && owner === seatId;
              const name = classic42.territories.find((t) => t.id === id)?.name ?? id;
              const className = [
                'terr',
                mine ? 'mine' : '',
                selected === id ? 'selected' : '',
                targets?.has(id) ? 'target' : '',
              ].filter(Boolean).join(' ');

              // The row is a container, never a button, because callers put
              // steppers in `trailing` -- nesting those inside a button is
              // invalid HTML and makes them fight the row's click handler.
              // The selectable area is an inner button, so the steppers sit
              // outside it and act only on themselves.
              return (
                <div className={className} key={id}>
                  {onSelect ? (
                    <button type="button" className="terr-main" onClick={() => onSelect(id)}>
                      <span className="dot" style={{ background: colorOf(owner) }} aria-hidden="true" />
                      <span className="name truncate">{name}</span>
                      <span className="armies mono">{territory ? territory.armies : '\u2013'}</span>
                    </button>
                  ) : (
                    <span className="terr-main">
                      <span className="dot" style={{ background: colorOf(owner) }} aria-hidden="true" />
                      <span className="name truncate">{name}</span>
                      <span className="armies mono">{territory ? territory.armies : '\u2013'}</span>
                    </span>
                  )}
                  {trailing?.(id)}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
